/** Saved cards: see them, add one, remove one. */

import { del, errorMessage, get, post } from "@taxi/mobile-web/api";
import { useResource } from "@taxi/mobile-web/hooks";
import { CardIcon, PlusIcon, TrashIcon } from "@taxi/mobile-web/icons";
import {
  Button,
  Empty,
  IconButton,
  Loading,
  Notice,
  Sheet,
  TopBar,
  useToast,
} from "@taxi/mobile-web/ui";
import { useRef, useState } from "react";
import { PaymentForm, type PaymentFormHandle } from "../components/payment-form";
import type { PaymentSheet, SavedCard } from "../lib/types";

const brandName = (brand: string) => brand.charAt(0).toUpperCase() + brand.slice(1);

export function CardsScreen() {
  const cards = useResource(() => get<{ data: SavedCard[] }>("/customer/payment-methods"), []);
  const toast = useToast();
  const [sheet, setSheet] = useState<PaymentSheet | null>(null);
  const [starting, setStarting] = useState(false);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useRef<PaymentFormHandle>(null);

  async function startAdding() {
    setStarting(true);
    setError(null);
    try {
      setSheet(await post<PaymentSheet>("/customer/payment-methods/setup"));
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setStarting(false);
    }
  }

  async function save() {
    setBusy(true);
    setError(null);
    const problem = await form.current!.confirm();
    setBusy(false);
    if (problem) {
      setError(problem);
      return;
    }
    setSheet(null);
    toast("Card saved");
    await cards.reload();
  }

  async function remove(card: SavedCard) {
    try {
      await del(`/customer/payment-methods/${encodeURIComponent(card.id)}`);
      toast("Card removed");
      await cards.reload();
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  const list = cards.data?.data ?? [];
  return (
    <div className="cards">
      <TopBar title="Payment cards" back="/account" />
      <div className="screen-body">
        {cards.loading && !cards.data ? (
          <Loading />
        ) : list.length === 0 ? (
          <Empty icon={<CardIcon size={28} />} title="No saved cards">
            <p>Cards you pay with are saved here for next time.</p>
          </Empty>
        ) : (
          <ul className="saved-cards">
            {list.map((card) => (
              <li key={card.id} className={`saved-card saved-card-${card.brand}`}>
                <span className="saved-card-brand">{brandName(card.brand)}</span>
                <span className="saved-card-number">•••• •••• •••• {card.last4}</span>
                <span className="saved-card-expiry">
                  Expires {String(card.expMonth).padStart(2, "0")}/{String(card.expYear).slice(-2)}
                </span>
                <IconButton
                  label={`Remove card ending ${card.last4}`}
                  className="saved-card-remove"
                  onClick={() => void remove(card)}
                >
                  <TrashIcon size={18} />
                </IconButton>
              </li>
            ))}
          </ul>
        )}
        <Button variant="secondary" loading={starting} onClick={startAdding}>
          <PlusIcon size={18} /> Add a card
        </Button>
        <p className="fine-print">Card details are kept by our payment provider, never by us.</p>
      </div>
      <Sheet open={Boolean(sheet)} title="Add a card" onClose={() => setSheet(null)}>
        {sheet && (
          <>
            <PaymentForm
              ref={form}
              sheet={sheet}
              savedCards={false}
              returnUrl={`${window.location.origin}/account/cards`}
              onReady={setReady}
            />
            {error && <Notice tone="error">{error}</Notice>}
            <Button loading={busy} disabled={!ready} onClick={save}>
              Save card
            </Button>
          </>
        )}
      </Sheet>
    </div>
  );
}
